import ExcelJS from 'exceljs'
import { readCellText } from '../../../shared/utils/excelCellText'

export interface ManagerQuoteItem {
  requestItemId: string
  partNumber: string
  brand: string | null
  quantity: number | string
  comment: string | null
}

export interface ManagerQuoteOpts {
  clientCode: string | null
  requestNumber: string
  items: ManagerQuoteItem[]
}

export interface ParsedManagerQuote {
  requestItemId: string
  supplierName: string
  price: number
  // Стоки — валюта берётся из колонки «Валюта» (CNY по умолчанию, можно
  // поставить USD/EUR). РФ — всегда RUB.
  currency: 'RUB' | 'CNY' | 'USD' | 'EUR'
  sourceType: 'OTHER' | 'RF'
  note?: string
}

export interface ParsedManagerQuotesResult {
  quotes: ParsedManagerQuote[]
  // Сколько строк с заполненной ценой пропущено, потому что в служебной
  // колонке B нет ID позиции этой заявки (старый шаблон или чужой файл).
  rowsWithUnknownId: number
}

/**
 * Excel-шаблон для быстрого ввода квот менеджером.
 * Редактируемые столбцы на пару матриц (Стоки и РФ): поставщик, цена,
 * заметка. У Стоков есть отдельная колонка «Валюта» (по умолчанию CNY,
 * менеджер может сменить на USD/EUR любым регистром). РФ — всегда RUB.
 * Менеджер заполняет нужные колонки, при загрузке создаются квоты по
 * правилам:
 *   - есть цена → создаётся квота с указанной матрицей и валютой;
 *   - нет цены → строка пропускается;
 *   - поставщик пустой при заполненной цене → имя = "Цена менеджера".
 *
 * `requestItemId` хранится в скрытой служебной колонке (B) — это привязка
 * каждой строки к конкретной позиции при импорте, чтобы переставление
 * строк или дубликаты партномера не ломали логику. Парсер понимает и
 * старый формат файла (без колонки «Валюта») — там валюта Стоков = CNY.
 */
export async function generateManagerQuotesExcel(opts: ManagerQuoteOpts): Promise<void> {
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('Квоты')

  ws.columns = [
    { key: 'n', width: 5 },
    { key: 'requestItemId', width: 22 },
    { key: 'partNumber', width: 24 },
    { key: 'brand', width: 18 },
    { key: 'qty', width: 10 },
    { key: 'note', width: 28 },
    { key: 'supplierStock', width: 22 },
    { key: 'priceStock', width: 16 },
    { key: 'currencyStock', width: 12 },
    { key: 'noteStock', width: 28 },
    { key: 'supplierRf', width: 22 },
    { key: 'priceRf', width: 16 },
    { key: 'noteRf', width: 28 },
  ]

  const headers = [
    '№',
    'ID позиции',
    'Артикул',
    'Бренд',
    'Кол-во',
    'Заметка',
    'Поставщик Стоки',
    'Цена Стоки',
    'Валюта',
    'Заметка Стоки',
    'Поставщик РФ',
    'Цена РФ (RUB)',
    'Заметка РФ',
  ]

  const border: Partial<ExcelJS.Borders> = {
    top: { style: 'thin', color: { argb: 'FF000000' } },
    left: { style: 'thin', color: { argb: 'FF000000' } },
    bottom: { style: 'thin', color: { argb: 'FF000000' } },
    right: { style: 'thin', color: { argb: 'FF000000' } },
  }
  const headerFill: ExcelJS.Fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFD9E1F2' },
  }
  const headerFont: Partial<ExcelJS.Font> = { name: 'Times New Roman', size: 10, bold: true }
  const bodyFont: Partial<ExcelJS.Font> = { name: 'Times New Roman', size: 10 }

  const headerRow = ws.getRow(1)
  headers.forEach((h, i) => {
    const c = headerRow.getCell(i + 1)
    c.value = h
    c.font = headerFont
    c.fill = headerFill
    c.border = border
    c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
  })
  headerRow.height = 30

  for (let i = 0; i < opts.items.length; i++) {
    const item = opts.items[i]!
    const row = ws.getRow(i + 2)
    row.height = 20
    const vals: (string | number)[] = [
      i + 1,
      item.requestItemId,
      item.partNumber,
      item.brand ?? '',
      Number(item.quantity),
      item.comment ?? '',
      '', // Поставщик Стоки
      '', // Цена Стоки
      'CNY', // Валюта — по умолчанию юани, менеджер может сменить на USD/EUR
      '', // Заметка Стоки
      '', // Поставщик РФ
      '', // Цена РФ
      '', // Заметка РФ
    ]
    vals.forEach((v, j) => {
      const c = row.getCell(j + 1)
      c.value = v
      c.font = bodyFont
      c.border = border
      c.alignment = { vertical: 'middle', wrapText: true }
    })
  }

  // Скрываем служебный ID позиции — пользователю он не нужен, но при импорте
  // мы его читаем (поэтому только hidden, не protected).
  ws.getColumn(2).hidden = true

  const code = opts.clientCode ?? 'NO_CODE'
  const fileName = `${code} - ${opts.requestNumber}.xlsx`
  const buffer = await wb.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Парсит загруженный менеджером Excel и возвращает квоты для bulk-create.
 * Обрабатывает обе пары колонок (Стоки + РФ) — каждая строка может дать
 * 0, 1 или 2 квоты в зависимости от заполненных цен. Без цены строка
 * игнорируется.
 *
 * Привязка строки к позиции — ТОЛЬКО по служебной колонке B (ID позиции),
 * которую генерирует `generateManagerQuotesExcel`. Чекбоксы на странице
 * заявки решают лишь то, какие позиции попадут в скачиваемый шаблон, и НЕ
 * влияют на загрузку: программа сама разносит квоты по ID из файла. Если в
 * строке нет ID, принадлежащего этой заявке (старый шаблон без скрытой
 * колонки или файл из другой заявки) — строка пропускается, чтобы квота не
 * приклеилась к случайной позиции.
 */
export async function parseManagerQuotesExcel(
  file: File,
  requestItemIds: string[],
): Promise<ParsedManagerQuotesResult> {
  const wb = new ExcelJS.Workbook()
  await wb.xlsx.load(await file.arrayBuffer())
  const ws = wb.worksheets[0]
  if (!ws) return { quotes: [], rowsWithUnknownId: 0 }

  const out: ParsedManagerQuote[] = []
  let rowsWithUnknownId = 0
  // Допустимые ID — все позиции этой заявки. Строка привязывается к позиции
  // только если её ID из колонки B входит в этот набор.
  const validIds = new Set(requestItemIds)

  // Определяем формат файла по шапке. В новом формате есть колонка «Валюта»
  // (после «Цена Стоки»), из-за чего РФ-колонки сдвинуты на 1. Старые файлы
  // (скачанные до добавления валюты) — без неё, валюта Стоков = CNY.
  const headerRow = ws.getRow(1)
  let currencyCol = 0
  for (let i = 1; i <= ws.columnCount; i++) {
    if (readCellText(headerRow.getCell(i)).toLowerCase() === 'валюта') {
      currencyCol = i
      break
    }
  }
  const isNewFormat = currencyCol > 0
  // Индексы колонок: новый формат — 8/9/10 + 11/12/13, старый — 8/9 + 10/11/12.
  const COL = isNewFormat
    ? { priceStock: 8, noteStock: 10, supplierRf: 11, priceRf: 12, noteRf: 13 }
    : { priceStock: 8, noteStock: 9, supplierRf: 10, priceRf: 11, noteRf: 12 }

  const readStockCurrency = (row: ExcelJS.Row): 'CNY' | 'USD' | 'EUR' => {
    if (!isNewFormat) return 'CNY' // старый файл — валюта Стоков всегда юани
    const raw = readCellText(row.getCell(currencyCol)).toUpperCase()
    return raw === 'USD' || raw === 'EUR' ? raw : 'CNY'
  }

  ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === 1) return // header

    const itemId = readCellText(row.getCell(2))

    const supplierStock = readCellText(row.getCell(7))
    const priceStock = parseNumber(row.getCell(COL.priceStock))
    const noteStock = readCellText(row.getCell(COL.noteStock))

    const supplierRf = readCellText(row.getCell(COL.supplierRf))
    const priceRf = parseNumber(row.getCell(COL.priceRf))
    const noteRf = readCellText(row.getCell(COL.noteRf))

    const hasPrice = (priceStock != null && priceStock > 0) || (priceRf != null && priceRf > 0)

    // Привязка строго по ID из колонки B. Нет валидного ID — строку с ценой
    // считаем «потерянной» (не угадываем позицию по чекбоксам/порядку строк).
    if (!itemId || !validIds.has(itemId)) {
      if (hasPrice) rowsWithUnknownId++
      return
    }

    if (priceStock != null && priceStock > 0) {
      out.push({
        requestItemId: itemId,
        supplierName: supplierStock || 'Цена менеджера',
        price: priceStock,
        currency: readStockCurrency(row),
        sourceType: 'OTHER',
        note: noteStock || undefined,
      })
    }
    if (priceRf != null && priceRf > 0) {
      out.push({
        requestItemId: itemId,
        supplierName: supplierRf || 'Цена менеджера',
        price: priceRf,
        currency: 'RUB',
        sourceType: 'RF',
        note: noteRf || undefined,
      })
    }
  })

  return { quotes: out, rowsWithUnknownId }
}

function parseNumber(cell: ExcelJS.Cell): number | null {
  const value = cell.value
  if (typeof value === 'number') return isFinite(value) ? value : null
  // Не-числовые формы (строка, формула, rich text) — через общий ридер текста.
  const raw = readCellText(cell)
  if (!raw) return null
  const n = parseFloat(raw.replace(/\s/g, '').replace(',', '.'))
  return isFinite(n) ? n : null
}
